/*
  Checkout gate — Anima Domus warehouse sale.
  Keeps every "#CheckOut" button disabled until the required pre-checkout
  fields are valid, and saves the answers to the cart as attributes so they
  show on the order in Shopify admin ("Additional details").

  Fields (rendered by snippets/cart-checkout-gate.liquid):
    - Assisted by salesperson (Yes/No)  -> attributes[Assisted by salesperson]
    - Salesperson (dropdown, if Yes)     -> attributes[Salesperson]
    - Interior design company            -> attributes[Interior design company]
        always visible; REQUIRED only during the designer preview window
        (or when forced on in Theme settings), optional otherwise
    - Terms of Sale checkbox             -> attributes[Terms of Sale accepted]

  The element is re-created every time the theme re-renders the cart
  (#AjaxCartSubtotal.innerHTML is replaced), so all state lives in the DOM
  and in the cart attributes, never in this class.
*/
if (typeof CheckoutGate !== 'function') {

  class CheckoutGate extends HTMLElement {

    constructor() {
      super();

      this.assistedInputs = this.querySelectorAll('[data-gate-assisted]');
      this.salespersonField = this.querySelector('[data-gate-field="salesperson"]');
      this.salespersonSelect = this.querySelector('[data-gate-salesperson]');
      this.companyField = this.querySelector('[data-gate-field="company"]');
      this.companyInput = this.querySelector('[data-gate-company]');
      this.termsCheckbox = this.querySelector('[data-gate-terms]');
      this.termsAttribute = this.querySelector('[data-gate-terms-attribute]');
      this.notice = this.querySelector('[data-gate-notice]');

      this.companyRequiredMark = this.querySelector('[data-gate-company-required]');
      this.companyOptionalMark = this.querySelector('[data-gate-company-optional]');
      this.companyHelp = this.querySelector('[data-gate-company-help]');

      // The company field is always shown; this only decides whether it is required.
      this.designerRequired = this.#resolveDesignerWindow();
      this.pendingSave = null;
      this.saveTimer = null;

      if (this.companyRequiredMark) this.companyRequiredMark.hidden = !this.designerRequired;
      if (this.companyOptionalMark) this.companyOptionalMark.hidden = this.designerRequired;
      if (this.companyHelp) this.companyHelp.hidden = !this.designerRequired;
      if (this.companyInput) this.companyInput.required = this.designerRequired;

      this.assistedInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.#toggleSalesperson();
          this.#queueSave();
          this.#toggleCheckoutButtons();
        });
      });

      if (this.salespersonSelect) {
        this.salespersonSelect.addEventListener('change', () => {
          this.#queueSave();
          this.#toggleCheckoutButtons();
        });
      }

      if (this.companyInput) {
        this.companyInput.addEventListener('input', () => {
          this.#queueSave();
          this.#toggleCheckoutButtons();
        });
      }

      if (this.termsCheckbox) {
        this.termsCheckbox.addEventListener('change', () => {
          if (this.termsAttribute) {
            this.termsAttribute.value = this.termsCheckbox.checked ? this.#acceptanceStamp() : '';
          }
          this.#queueSave();
          this.#toggleCheckoutButtons();
        });
      }

      // Belt and braces: if a user manages to click a disabled-looking button
      // (or the browser ignores `disabled`), block the submit.
      this.#checkoutButtons().forEach(button => {
        if (!button.dataset.gateBound) {
          button.dataset.gateBound = 'true';
          button.addEventListener('click', (e) => {
            const scope = button.closest('.cart__details') || document;
            const gate = scope.querySelector('checkout-gate');
            if (gate && !gate.isValid()) {
              e.preventDefault();
              gate.showNotice();
            }
          });
        }
      });

      this.#toggleSalesperson();
      this.#toggleCheckoutButtons();
    }

    /* ---------- validation ---------- */

    isValid() {
      if (this.assistedInputs.length) {
        const assisted = this.#assistedValue();
        if (!assisted) return false;
        if (assisted === 'Yes' && this.salespersonSelect && !this.salespersonSelect.value) return false;
      }
      if (this.designerRequired && this.companyInput && this.companyInput.value.trim().length < 2) return false;
      if (this.termsCheckbox && !this.termsCheckbox.checked) return false;
      if (this.pendingSave) return false;
      return true;
    }

    showNotice() {
      if (this.notice) {
        this.notice.hidden = false;
      }
    }

    #assistedValue() {
      const checked = Array.from(this.assistedInputs).find(i => i.checked);
      return checked ? checked.value : '';
    }

    #toggleSalesperson() {
      if (!this.salespersonField) return;
      const yes = this.#assistedValue() === 'Yes';
      this.salespersonField.hidden = !yes;
      if (!yes && this.salespersonSelect) {
        this.salespersonSelect.value = '';
      }
    }

    // Only the checkout button(s) that belong to this cart view (page or drawer).
    #checkoutButtons() {
      const scope = this.closest('.cart__details') || document;
      return scope.querySelectorAll('#CheckOut');
    }

    #toggleCheckoutButtons() {
      const valid = this.isValid();
      this.#checkoutButtons().forEach(button => {
        button.disabled = !valid;
        button.setAttribute('aria-disabled', String(!valid));
      });
      if (valid && this.notice) {
        this.notice.hidden = true;
      }
    }

    /* ---------- designer preview window (is the company field required?) ---------- */

    #resolveDesignerWindow() {
      // Staff preview: add ?designer_preview=1 to any page URL to force the field
      // to "required" for this browser tab (?designer_preview=0 switches it off again).
      try {
        const param = new URLSearchParams(window.location.search).get('designer_preview');
        if (param === '1') sessionStorage.setItem('adDesignerPreview', '1');
        if (param === '0') sessionStorage.removeItem('adDesignerPreview');
        if (sessionStorage.getItem('adDesignerPreview') === '1') return true;
      } catch (e) { /* storage unavailable: ignore */ }

      const mode = (this.dataset.designerMode || 'auto').toLowerCase();
      if (mode === 'on') return true;
      if (mode === 'off' || mode === 'hidden') return false;

      const start = Date.parse(this.dataset.designerStart || '');
      const end = Date.parse(this.dataset.designerEnd || '');
      if (isNaN(start) || isNaN(end)) return false;

      const now = Date.now();
      return now >= start && now < end;
    }

    /* ---------- persistence ---------- */

    #acceptanceStamp() {
      try {
        const stamp = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          year: 'numeric', month: 'short', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: true
        }).format(new Date());
        return `Yes — ${stamp} ET`;
      } catch (e) {
        return `Yes — ${new Date().toISOString()}`;
      }
    }

    #collectAttributes() {
      const attributes = {};
      if (this.assistedInputs.length) {
        const assisted = this.#assistedValue();
        attributes['Assisted by salesperson'] = assisted || '';
        attributes['Salesperson'] = (assisted === 'Yes' && this.salespersonSelect) ? this.salespersonSelect.value : '';
      }
      if (this.companyInput) {
        attributes['Interior design company'] = this.companyInput.value.trim();
      }
      if (this.termsAttribute) {
        attributes['Terms of Sale accepted'] = this.termsAttribute.value || '';
      }
      return attributes;
    }

    #queueSave() {
      clearTimeout(this.saveTimer);
      // Mark a save as pending immediately so the button can't be clicked
      // between typing and the request finishing.
      if (!this.pendingSave) {
        this.pendingSave = true;
        this.#toggleCheckoutButtons();
      }
      this.saveTimer = setTimeout(() => this.#save(), 350);
    }

    #save() {
      const url = this.dataset.cartUpdateUrl || '/cart/update.js';
      const body = JSON.stringify({ attributes: this.#collectAttributes() });

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body
      })
        .catch(err => {
          // Non-fatal: the inputs also carry form="cart", so values are still
          // posted with the checkout submit if the AJAX call fails.
          console.warn('checkout-gate: could not save cart attributes', err);
        })
        .finally(() => {
          this.pendingSave = null;
          this.#toggleCheckoutButtons();
        });
    }
  }

  if (typeof customElements.get('checkout-gate') === 'undefined') {
    customElements.define('checkout-gate', CheckoutGate);
  }
}
